import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const slides = [
    {
      headline: "Atlas Cyber Shield™",
      description: "Built to detect, defend, and neutralize — Atlas Cyber Shield delivers enterprise-grade protection powered by AI-driven threat intelligence.",
      cta: "Activate Protection",
      ctaLink: "/services/cybersecurity/defensive-security",
    },
    {
      headline: "24/7 Security Operations",
      description: "Round-the-clock threat monitoring by elite analysts. Our SOC never sleeps — so your business stays secure, always.",
      cta: "Start Monitoring",
      ctaLink: "/services/cybersecurity/soc-as-a-service",
    },
    {
      headline: "Zero Trust. Zero Compromise.",
      description: "Architect your infrastructure with zero-trust principles. Verify everything, trust nothing — from endpoints to the cloud.",
      cta: "Explore Architecture",
      ctaLink: "/services/infrastructure/network-engineering",
    },
    {
      headline: "Break In Before They Do.",
      description: "Our red team simulates real-world attacks to uncover critical vulnerabilities. Offensive security that strengthens your defense posture.",
      cta: "Challenge Your Defenses",
      ctaLink: "/services/cybersecurity/offensive-security",
    },
    {
      headline: "Next-Gen Data Centers",
      description: "Modern, hyper-converged data center infrastructure — designed for scale, secured by default, built to evolve with your business.",
      cta: "Talk to an Architect",
      ctaLink: "/services/infrastructure/data-center-design",
    },
  ];

  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // Auto-rotate slides
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(goToNextSlide, 6000);
    return () => clearInterval(interval);
  }, [isPaused, goToNextSlide]);

  // Partner logos for the bottom bar
  const partnerLogos = [
    { name: 'Cisco', logo: '/Logos/Cisco_logo.svg' },
    { name: 'Fortinet', logo: '/Logos/Fortinet_logo.svg' },
    { name: 'CrowdStrike', logo: '/Logos/CrowdStrike_logo.svg' },
    { name: 'Palo Alto', logo: '/Logos/paloalto_logo.svg' },
    { name: 'Sophos', logo: '/Logos/Sophos_logo.svg' },
    { name: 'SentinelOne', logo: '/Logos/SentinelOne_logo.svg' },
    { name: 'Juniper', logo: '/Logos/Juniper_Networks_logo.svg' },
  ];

  return (
    <section className="relative bg-black overflow-hidden">
      {/* ═══════════════════════════════════════════════════════════
          HERO MAIN AREA
      ═══════════════════════════════════════════════════════════ */}
      <div className="relative min-h-[85vh] lg:min-h-[90vh] flex items-center">

        {/* ─── Background: MDR STYLE (High-Impact Blue Gradient Blend) ─── */}
        <div
          className="absolute inset-0 z-0 overflow-hidden"
          style={{
            background: `
              radial-gradient(ellipse 60% 90% at 100% 50%, #3b82f6 0%, #1d4ed8 22%, #1e3a8a 45%, #0f172a 70%, #070b18 100%),
              linear-gradient(to right, #060b18 0%, #0f172a 30%, #1e3a8a 60%, #1d4ed8 100%)
            `,
            backgroundBlendMode: 'screen',
          }}
        >
          {/* Subtle grid for tech feel */}
          <div
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59,130,246,0.2) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59,130,246,0.2) 1px, transparent 1px)
              `,
              backgroundSize: '100px 100px',
            }}
          />
        </div>

        {/* ─── Content: MDR SPLIT LAYOUT (Balanced & Professional) ─── */}
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 relative z-10 w-full min-h-[85vh] flex items-center">
          <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20 translate-y-6 md:translate-y-8">

            {/* LEFT: Text slides (Dynamic) */}
            <div className="w-full lg:w-[55%]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 30 }}
                  transition={{ duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="space-y-6"
                >
                  {/* Headline */}
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[3.8rem] font-black leading-[1.05] tracking-tight text-white drop-shadow-sm">
                    {slides[currentSlide].headline}
                  </h1>

                  {/* Description */}
                  <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl font-light">
                    {slides[currentSlide].description}
                  </p>

                  {/* CTA Button — Solid MDR Style */}
                  <div className="pt-3">
                    <Link
                      to={slides[currentSlide].ctaLink}
                      className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold uppercase tracking-wider px-8 py-4 rounded transition-all duration-300 shadow-xl shadow-blue-900/40 hover:shadow-blue-600/50 active:scale-[0.97]"
                    >
                      {slides[currentSlide].cta}
                      <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Slide Indicators — Bottom Left */}
              <div className="flex items-center gap-3 mt-16 md:mt-24">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    className="group relative py-2"
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <div className={`h-[2px] rounded-full transition-all duration-500 ${currentSlide === index ? 'w-10 bg-white' : 'w-4 bg-white/25 group-hover:bg-white/50'
                      }`} />
                  </button>
                ))}
              </div>
            </div>

            {/* RIGHT: Atlas Defenders Brand Mark (STATIC/STATIONARY) */}
            {/* Perfectly centered in the blue glow area, high-impact enterprise size */}
            <div className="hidden lg:flex flex-1 items-center justify-center relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, x: 100, y: -80 }}
                animate={{ opacity: 1, scale: 1, x: 100, y: -80 }}
                transition={{ duration: 1.5, delay: 0.4, ease: "easeOut" }}
                className="w-full max-w-[500px] xl:max-w-[650px] select-none"
              >
                <img
                  src="/Logos/atlasdefenders_white_logo.svg"
                  alt="Atlas Defenders"
                  className="w-full h-auto drop-shadow-[0_0_80px_rgba(59,130,246,0.35)] animate-pulse-slow active:scale-[0.98] transition-all duration-700"
                  style={{ animationDuration: '8s' }}
                />
              </motion.div>
            </div>
          </div>
        </div>

        {/* ─── Navigation Arrows ─── */}
        <motion.button
          onClick={goToPrevSlide}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 rounded-full border border-white/10 bg-black/60 hover:bg-white/10 hover:border-white/20 text-white/50 hover:text-white transition-all duration-300"
          whileHover={{ x: -3 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>

        <motion.button
          onClick={goToNextSlide}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 rounded-full border border-white/10 bg-black/60 hover:bg-white/10 hover:border-white/20 text-white/50 hover:text-white transition-all duration-300"
          whileHover={{ x: 3 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
          </svg>
        </motion.button>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          TRUSTED BY — Partner Logos Bar (like Trend Micro bottom)
      ═══════════════════════════════════════════════════════════ */}
      <div className="relative z-10 border-t border-white/[0.06] bg-black">
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 py-6">
          <div className="flex flex-wrap items-center justify-between gap-8">
            {partnerLogos.map((partner) => (
              <div key={partner.name} className="flex items-center justify-center h-8 opacity-40 hover:opacity-70 transition-opacity duration-300 grayscale hover:grayscale-0">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-full max-w-[100px] object-contain brightness-0 invert"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
